import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Clock,
  Star,
  MessageSquare,
  Zap,
  Wrench,
  Sparkles,
  Truck,
  MoreHorizontal,
  CreditCard,
  Loader2,
  Send,
  Users,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const categoryData = {
  plumbing: { icon: Wrench, color: 'bg-blue-500', label: 'Plumbing' },
  cleaning: { icon: Sparkles, color: 'bg-emerald-500', label: 'Cleaning' },
  delivery: { icon: Truck, color: 'bg-amber-500', label: 'Delivery' },
  others: { icon: MoreHorizontal, color: 'bg-purple-500', label: 'Others' }
};

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();

  const [job, setJob] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [applications, setApplications] = useState([]);
  const [myApplication, setMyApplication] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactLoading, setContactLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [showApplyDialog, setShowApplyDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch job
        const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });

        if (!response.ok) throw new Error('Job not found');
        
        const data = await response.json();
        setJob(data);

        // Fetch employer info
        if (data.employer_id) {
          const empResponse = await fetch(`${API_URL}/api/users/${data.employer_id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            credentials: 'include'
          });
          if (empResponse.ok) {
            setEmployer(await empResponse.json());
          }
        }

        // Fetch applications if employer
        if (user && data.employer_id === user.user_id) {
          const appsResponse = await fetch(`${API_URL}/api/jobs/${jobId}/applications`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (appsResponse.ok) {
            setApplications(await appsResponse.json());
          }
        }

        // Fetch my application if worker
        if (user && user.role === 'worker') {
          const myAppsResponse = await fetch(`${API_URL}/api/applications/my`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (myAppsResponse.ok) {
            const myApps = await myAppsResponse.json();
            const myApp = myApps.find(a => a.job_id === jobId);
            setMyApplication(myApp || null);
          }
        }

        // Fetch payment status
        const paymentResponse = await fetch(`${API_URL}/api/payments/job/${jobId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });
        if (paymentResponse.ok) {
          const paymentData = await paymentResponse.json();
          setPayment(paymentData);
        }

      } catch (error) {
        toast.error(t('common.error'));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobId, navigate, t, token, user]);

  const handleContact = async () => {
    if (!user || !job) return;

    setContactLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          job_id: job.job_id,
          participant_id: job.employer_id
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to create chat');

      const chat = await response.json();
      navigate(`/chat/${chat.chat_id}`);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setContactLoading(false);
    }
  };

  const handleApply = async () => {
    if (!user || !job) return;

    setApplyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          job_id: jobId,
          message: applyMessage || null
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply');
      }

      const application = await response.json();
      setMyApplication(application);
      setShowApplyDialog(false);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setApplyLoading(false);
    }
  };

  const handleApplicationAction = async (applicationId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/applications/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to update application');

      const updated = await response.json();
      setApplications(prev =>
        prev.map(a => a.application_id === applicationId ? updated : a)
      );

      // If accepted, update job status locally
      if (status === 'accepted') {
        setJob(prev => ({ ...prev, status: 'in_progress', assigned_worker_id: updated.worker_id }));
      }

      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handlePayment = async () => {
    if (!job) return;

    setPayLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          job_id: job.job_id,
          origin_url: window.location.origin
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create payment');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      toast.error(error.message);
      setPayLoading(false);
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    
    if (diff < 60) return `${diff} minutes ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hours ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!job) return null;

  const category = categoryData[job.category] || categoryData.others;
  const CategoryIcon = category.icon;
  const isEmployer = user?.user_id === job.employer_id;
  const isAssignedWorker = user?.user_id === job.assigned_worker_id;
  const canApply = user?.role === 'worker' && !isEmployer && job.status === 'open' && !myApplication;
  const showPayButton = isEmployer && job.status === 'in_progress' && job.assigned_worker_id && (!payment || payment.payment_status !== 'paid');

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('common.back')}</span>
        </button>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Job Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-16 h-16 ${category.color} rounded-xl flex items-center justify-center shrink-0`}>
                  <CategoryIcon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-slate-900">{job.title}</h1>
                      <p className="text-slate-500 mt-1 capitalize">{category.label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.urgent && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {t('jobs.urgent')}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="capitalize">
                        {t(`jobs.status_${job.status}`)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget */}
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl mb-6">
                <DollarSign className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="text-sm text-emerald-700">{t('jobs.budget')}</p>
                  <p className="text-2xl font-bold text-emerald-600">${job.budget}</p>
                </div>
                {payment?.payment_status === 'paid' && (
                  <Badge className="ml-auto bg-emerald-500">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('payment.paid')}
                  </Badge>
                )}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">{t('jobs.details')}</h2>
                <p className="text-slate-600 whitespace-pre-wrap">{job.description}</p>
              </div>

              {/* Payment Methods */}
              {job.payment_methods?.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {t('jobs.payment_methods')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {job.payment_methods.map((method) => (
                      <span
                        key={method}
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg capitalize font-medium"
                      >
                        {t(`job_form.${method}`) || method}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center gap-6 mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{timeAgo(job.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Applications Section (for employers) */}
            {isEmployer && applications.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t('applications.view_applications')} ({applications.length})
                </h2>
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div
                      key={app.application_id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={app.worker_photo} />
                        <AvatarFallback>{app.worker_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{app.worker_name}</p>
                          {app.worker_rating > 0 && (
                            <div className="flex items-center gap-1 text-sm">
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                              <span>{app.worker_rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        {app.message && (
                          <p className="text-sm text-slate-500 truncate">{app.message}</p>
                        )}
                        <Badge
                          variant="secondary"
                          className={`mt-1 ${
                            app.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                            app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {t(`applications.${app.status}`)}
                        </Badge>
                      </div>
                      {app.status === 'pending' && job.status === 'open' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            data-testid={`accept-application-${app.application_id}`}
                            onClick={() => handleApplicationAction(app.application_id, 'accepted')}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t('applications.accept')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`reject-application-${app.application_id}`}
                            onClick={() => handleApplicationAction(app.application_id, 'rejected')}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('applications.reject')}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location Map */}
            {job.location?.lat && job.location?.lng && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-slate-100">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-500" />
                    {t('job_form.location')}
                  </h2>
                  {job.location?.address && (
                    <p className="text-slate-500 mt-1">{job.location.address}</p>
                  )}
                </div>
                <div className="h-64">
                  <MapContainer
                    center={[job.location.lat, job.location.lng]}
                    zoom={14}
                    className="h-full w-full"
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[job.location.lat, job.location.lng]}>
                      <Popup>{job.title}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Employer Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('jobs.posted_by')}</h2>
              
              {employer ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={employer.photo_url} />
                      <AvatarFallback className="bg-slate-200 text-lg">
                        {employer.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{employer.name}</p>
                      {employer.verified && (
                        <Badge variant="secondary" className="mt-1">
                          {t('profile.verified')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {employer.rating > 0 && (
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                      <span className="font-semibold">{employer.rating.toFixed(1)}</span>
                      <span className="text-slate-500">({employer.review_count} reviews)</span>
                    </div>
                  )}

                  <Button
                    data-testid="view-employer-profile-btn"
                    variant="outline"
                    onClick={() => navigate(`/profile/${employer.user_id}`)}
                    className="w-full"
                  >
                    {t('profile.title')}
                  </Button>
                </div>
              ) : (
                <div className="animate-pulse space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-slate-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="w-24 h-4 bg-slate-200 rounded"></div>
                      <div className="w-16 h-3 bg-slate-200 rounded"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* My Application Status (for workers) */}
            {myApplication && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">{t('applications.title')}</h2>
                <Badge
                  className={`${
                    myApplication.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                    myApplication.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}
                >
                  {myApplication.status === 'accepted' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {myApplication.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                  {myApplication.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                  {t(`applications.${myApplication.status}`)}
                </Badge>
                {myApplication.message && (
                  <p className="text-sm text-slate-500 mt-2">"{myApplication.message}"</p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            {/* Apply Button (for workers) */}
            {canApply && (
              <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="apply-job-btn"
                    className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {t('jobs.apply')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('applications.apply')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        {t('applications.apply_message')}
                      </label>
                      <Textarea
                        data-testid="apply-message-input"
                        value={applyMessage}
                        onChange={(e) => setApplyMessage(e.target.value)}
                        placeholder={t('applications.apply_placeholder')}
                        rows={4}
                        className="mt-2"
                      />
                    </div>
                    <Button
                      data-testid="submit-application-btn"
                      onClick={handleApply}
                      disabled={applyLoading}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                    >
                      {applyLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Send className="w-5 h-5 mr-2" />
                      )}
                      {t('common.submit')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Contact Button (for workers who got accepted) */}
            {isAssignedWorker && (
              <Button
                data-testid="contact-employer-btn"
                onClick={handleContact}
                disabled={contactLoading}
                className="w-full py-6 bg-slate-900 hover:bg-slate-800 text-white text-lg font-semibold"
              >
                {contactLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <MessageSquare className="w-5 h-5 mr-2" />
                )}
                {t('jobs.contact')}
              </Button>
            )}

            {/* Pay Worker Button (for employers) */}
            {showPayButton && (
              <Button
                data-testid="pay-worker-btn"
                onClick={handlePayment}
                disabled={payLoading}
                className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold"
              >
                {payLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-5 h-5 mr-2" />
                )}
                {t('payment.pay_now')} - ${job.budget}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
